import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { Spinner } from "./components/ui";
import Layout from "./components/Layout";
import LoginPage from "./pages/Login";
import ChoosePasswordPage from "./pages/ChoosePassword";
import DashboardPage from "./pages/Dashboard";
import CardsPage from "./pages/Cards";
import TransactionsPage from "./pages/Transactions";
import SlipsPage from "./pages/Slips";
import EmailPage from "./pages/Email";
import SharedPage from "./pages/Shared";
import ImportPage from "./pages/Import";
import OptimizerPage from "./pages/Optimizer";
import MilestonesPage from "./pages/Milestones";
import PointsPage from "./pages/Points";
import TransfersPage from "./pages/Transfers";
import SettingsPage from "./pages/Settings";
import GuidePage from "./pages/Guide";
import FeedbackPage from "./pages/Feedback";

export default function App() {
  const { user, loading, offline, retry } = useAuth();

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner label="Starting up…" />
      </div>
    );
  }

  // Still signed in, but nothing answered. Showing the login form here would
  // be a lie — the password is not the problem and typing it again cannot help.
  if (offline && !user) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="card-surface max-w-md p-6 text-center">
          <h1 className="text-lg font-semibold text-ink-950">The server isn&apos;t answering</h1>
          <p className="mt-2 text-sm text-ink-600">
            Your sign-in is still valid — the app just can&apos;t reach the server that holds your data.
            If you started it in a terminal, check that window is still open and the command still
            running, then try again.
          </p>
          <div className="mt-5 flex justify-center gap-2">
            <button className="btn-primary" onClick={retry}>Try again</button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // The server refuses everything else until this is done, so nothing else
  // is shown either.
  if (user.mustChangePassword) return <ChoosePasswordPage />;

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/optimizer" element={<OptimizerPage />} />
        <Route path="/transactions" element={<TransactionsPage />} />
        <Route path="/slips" element={<SlipsPage />} />
        <Route path="/email" element={<EmailPage />} />
        <Route path="/shared" element={<SharedPage />} />
        <Route path="/import" element={<ImportPage />} />
        <Route path="/cards" element={<CardsPage />} />
        <Route path="/milestones" element={<MilestonesPage />} />
        <Route path="/points" element={<PointsPage />} />
        <Route path="/transfers" element={<TransfersPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/guide" element={<GuidePage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
